import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, forkJoin, of, switchMap } from 'rxjs';
import { TreeNode } from 'primeng/api';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { DDL } from 'src/app/core-module/models/ddl';
import { AuditLocation } from 'src/app/core-module/models/Locations/audit-location';
import { FeaturesList } from 'src/app/core-module/models/Locations/features-list';
import { NewLocationWizardRequest } from 'src/app/core-module/models/Locations/new-location-wizard-request';
import { NewLocationWizardResult } from 'src/app/core-module/models/Locations/new-location-wizard-result';
import { LocationAudit } from 'src/app/core-module/models/UserManagement/location-audit';
import { PermissionsUser } from 'src/app/core-module/models/UserManagement/permissions-user';
import { UserLocation } from 'src/app/core-module/models/UserManagement/user-location';
import { UserLocationsHolder } from 'src/app/core-module/models/UserManagement/user-locations-holder';
import { UserManagementResult } from 'src/app/core-module/models/UserManagement/user-management-result';
import { UserPermissionsLocationListRequest } from 'src/app/core-module/models/UserManagement/user-permissions-location-list-request';
import { UserPermissionsRequest } from 'src/app/core-module/models/UserManagement/user-permissions-request';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { LocationManagementService } from 'src/app/core-module/services/location-management.service';
import { ReferenceService } from 'src/app/core-module/services/reference.service';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';

type WizardUser = BasicUser & {
  Features: FeaturesList;
  Locked: boolean;
};

@Component({
  selector: 'app-new-location-wizard',
  templateUrl: './new-location-wizard.component.html',
  styleUrl: './new-location-wizard.component.scss',
  standalone: false
})
export class NewLocationWizardComponent implements OnInit {
  currentStep = 1;
  detailsLoading = true;
  auditsLoading = false;
  usersLoading = false;
  saving = false;

  locations: AuditLocation[] = [];
  parentLocationNodes: TreeNode[] = [];
  parentLocationSelection: TreeNode | null = null;
  auditLocationNodes: TreeNode[] = [];
  auditCopyLocationSelection: TreeNode | null = null;
  frequencies: DDL[] = [];
  userLocations = new UserLocationsHolder();

  request = new NewLocationWizardRequest();
  createdLocation: AuditLocation | null = null;

  auditCopyLocationID = -1;
  audits: LocationAudit[] = [];
  allAudits = false;

  userCopyLocationID = -1;
  users: WizardUser[] = [];
  userFilter = '';
  usersReady = false;
  usersLoadError = false;

  readonly permissionOptions: { field: keyof FeaturesList; label: string }[] = [
    { field: 'SimpleSaveAudit', label: 'Complete audits' },
    { field: 'SimpleViewAudit', label: 'View audits and reports' },
    { field: 'SimpleCompleteAction', label: 'Complete actions' },
    { field: 'SimpleViewAction', label: 'View actions' },
    { field: 'SimpleAdmin', label: 'Administration' }
  ];

  constructor(
    private locationsService: LocationManagementService,
    private usersService: UserManagementService,
    private referenceService: ReferenceService,
    private toast: ToastrService,
    private router: Router,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.usersService.getEditableLocations()
      .pipe(finalize(() => this.detailsLoading = false))
      .subscribe({
        next: locations => {
          this.locations = locations
            .filter(location => location.Active)
            .sort((a, b) => a.LocationName.localeCompare(b.LocationName));
          this.buildParentLocationNodes();
        },
        error: () => {
          this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to load the available locations.' }));
        }
      });

    this.referenceService.getFrequencies().subscribe({
      next: frequencies => this.frequencies = frequencies,
      error: () => this.frequencies = []
    });
  }

  get filteredUsers(): WizardUser[] {
    const filter = this.userFilter.trim().toLowerCase();
    if (!filter) {
      return this.users;
    }
    return this.users.filter(user =>
      user.DisplayName.toLowerCase().includes(filter) ||
      user.Email.toLowerCase().includes(filter) ||
      user.Username.toLowerCase().includes(filter)
    );
  }

  get selectedUsers(): WizardUser[] {
    return this.users.filter(user => user.Selected);
  }

  get selectedAudits(): LocationAudit[] {
    return this.audits.filter(audit => audit.AssignedToLocation);
  }

  private buildParentLocationNodes(): void {
    const locationIDs = new Set(this.locations.map(location => location.LocationID));
    const childrenByParent = new Map<number, AuditLocation[]>();
    this.locations.forEach(location => {
      const parentID = locationIDs.has(location.ParentLocationID) ? location.ParentLocationID : -1;
      const children = childrenByParent.get(parentID) ?? [];
      children.push(location);
      childrenByParent.set(parentID, children);
    });
    childrenByParent.forEach(children => children.sort((a, b) =>
      a.Seq - b.Seq || a.LocationName.localeCompare(b.LocationName)
    ));

    const visited = new Set<number>();
    const buildChildren = (parentID: number): TreeNode[] => {
      return (childrenByParent.get(parentID) ?? []).map(location => {
        if (visited.has(location.LocationID)) {
          return null;
        }
        visited.add(location.LocationID);
        const children = buildChildren(location.LocationID);
        return {
          key: location.LocationID.toString(),
          label: location.LocationName,
          data: location.LocationID,
          expanded: true,
          children
        } as TreeNode;
      }).filter((node): node is TreeNode => node !== null);
    };

    const topLevelNode: TreeNode = {
      key: '-1',
      label: 'Top level',
      data: -1,
      expanded: true,
      children: buildChildren(-1)
    };
    this.parentLocationNodes = [topLevelNode];
    this.auditLocationNodes = topLevelNode.children ?? [];
    this.parentLocationSelection = topLevelNode;
  }

  selectParentLocation(selection: TreeNode | null): void {
    this.parentLocationSelection = selection ?? this.parentLocationNodes[0] ?? null;
    this.request.ParentLocationID = Number(this.parentLocationSelection?.data ?? -1);
  }

  get parentName(): string {
    return this.locations.find(location => location.LocationID === this.request.ParentLocationID)?.LocationName ?? 'Top level';
  }

  saveDetails(): void {
    if (this.saving || !this.request.LocationName.trim()) {
      return;
    }
    this.saving = true;
    this.locationsService.createLocationWizard(this.request)
      .pipe(finalize(() => {
        this.saving = false;
        this.changeDetector.detectChanges();
      }))
      .subscribe(result => this.handleLocationCreated(result));
  }

  private handleLocationCreated(result: NewLocationWizardResult): void {
    if (result.IsError || result.LocationID <= 0) {
      this.toast.addToastr(new Toastr({ Type: 3, Message: result.ErrorMessage || 'Unable to create the location.' }));
      return;
    }
    this.createdLocation = new AuditLocation(result.Location);
    this.createdLocation.LocationID = result.LocationID;
    this.toast.addToastr(new Toastr({ Type: 2, Message: 'Location details saved.' }));
    this.currentStep = 2;
    // The application uses a dynamic bootstrap and some HTTP completions can land
    // outside the view refresh which rendered the Details step.
    this.changeDetector.detectChanges();
    this.loadTargetAudits();
  }

  private loadTargetAudits(): void {
    if (!this.createdLocation) {
      return;
    }
    this.auditsLoading = true;
    this.usersService.getLocationAudits(this.createdLocation.LocationID)
      .pipe(finalize(() => {
        this.auditsLoading = false;
        this.changeDetector.detectChanges();
      }))
      .subscribe({
        next: audits => {
          this.audits = audits
            .map(audit => this.cloneAudit(audit, this.createdLocation!.LocationID))
            .sort((a, b) => a.AuditName.localeCompare(b.AuditName));
          this.updateAllAuditsState();
        },
        error: () => {
          this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to load audits for the new location.' }));
        }
      });
  }

  copyAuditsFrom(locationID: number): void {
    this.auditCopyLocationID = Number(locationID);
    if (this.auditCopyLocationID === -1) {
      this.loadTargetAudits();
      return;
    }
    this.auditsLoading = true;
    this.usersService.getLocationAudits(this.auditCopyLocationID)
      .pipe(finalize(() => {
        this.auditsLoading = false;
        this.changeDetector.detectChanges();
      }))
      .subscribe({
        next: sourceAudits => {
          const sourceByAudit = new Map(sourceAudits.map(audit => [audit.AuditID, audit]));
          this.audits = this.audits.map(target => {
            const source = sourceByAudit.get(target.AuditID);
            return source ? this.cloneAudit(source, this.createdLocation!.LocationID) : target;
          });
          this.updateAllAuditsState();
        },
        error: () => {
          this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to copy audits from that location.' }));
        }
      });
  }

  copyAuditsFromSelection(selection: TreeNode | null): void {
    this.auditCopyLocationSelection = selection;
    this.copyAuditsFrom(Number(selection?.data ?? -1));
  }

  private cloneAudit(audit: LocationAudit, locationID: number): LocationAudit {
    return new LocationAudit({
      ...audit,
      LocationID: locationID,
      Frequency: new DDL(audit.Frequency)
    });
  }

  setAllAudits(value: boolean): void {
    this.allAudits = value;
    this.audits.forEach(audit => audit.AssignedToLocation = value);
  }

  updateAllAuditsState(): void {
    this.allAudits = this.audits.length > 0 && this.audits.every(audit => audit.AssignedToLocation);
  }

  updateAuditFrequency(audit: LocationAudit, frequencyID: number): void {
    const frequency = this.frequencies.find(item => item.ID === Number(frequencyID));
    if (frequency) {
      audit.Frequency = new DDL(frequency);
    }
  }

  saveAudits(): void {
    if (this.saving || !this.createdLocation || this.audits.length === 0) {
      return;
    }
    this.saving = true;
    this.usersService.saveLocationAudits(this.audits)
      .pipe(finalize(() => {
        this.saving = false;
        this.changeDetector.detectChanges();
      }))
      .subscribe({
        next: result => {
          if (result.IsError) {
            this.toast.addToastr(new Toastr({ Type: 3, Message: result.ErrorMessage || 'Unable to save the location audits.' }));
            return;
          }
          this.toast.addToastr(new Toastr({ Type: 2, Message: 'Location audits saved.' }));
          this.currentStep = 3;
          this.loadUsersAndPermissions();
          this.changeDetector.detectChanges();
        },
        error: () => {
          this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to save the location audits.' }));
        }
      });
  }

  loadUsersAndPermissions(copyLocationID: number = -1): void {
    if (!this.createdLocation) {
      return;
    }
    this.userCopyLocationID = Number(copyLocationID);
    this.usersLoading = true;
    this.usersReady = false;
    this.usersLoadError = false;
    forkJoin({
      mappings: this.usersService.getUserLocationsList(false),
      targetPermissions: this.getPermissions(this.createdLocation.LocationID),
      sourcePermissions: this.userCopyLocationID === -1
        ? of(new UserManagementResult())
        : this.getPermissions(this.userCopyLocationID)
    }).pipe(finalize(() => {
      this.usersLoading = false;
      this.changeDetector.detectChanges();
    })).subscribe({
      next: ({ mappings, targetPermissions, sourcePermissions }) => {
        this.userLocations = mappings;
        const targetUserIDs = new Set(
          mappings.UserLocations.find(location => location.LocationID === this.createdLocation!.LocationID)?.Users.map(user => user.UserID) ?? []
        );
        const sourceUserIDs = this.userCopyLocationID === -1
          ? targetUserIDs
          : new Set(mappings.UserLocations.find(location => location.LocationID === this.userCopyLocationID)?.Users.map(user => user.UserID) ?? []);
        const targetFeatures = this.featuresByUser(targetPermissions, this.createdLocation!.LocationID);
        const sourceFeatures = this.featuresByUser(sourcePermissions, this.userCopyLocationID);
        const possibleUsers = [...mappings.PossibleUsers];
        mappings.UserLocations
          .find(location => location.LocationID === this.createdLocation!.LocationID)?.Users
          .forEach(user => {
            if (!possibleUsers.some(item => item.UserID === user.UserID)) {
              possibleUsers.push(user);
            }
          });

        this.users = possibleUsers.map(user => {
          const locked = targetUserIDs.has(user.UserID);
          const copiedFeatures = sourceFeatures.get(user.UserID);
          const features = locked
            ? targetFeatures.get(user.UserID) ?? new FeaturesList()
            : copiedFeatures ?? new FeaturesList();
          this.syncSimplePermissions(features);
          return Object.assign(new BasicUser(user), {
            Selected: locked || sourceUserIDs.has(user.UserID),
            Features: new FeaturesList(features),
            Locked: locked
          }) as WizardUser;
        }).sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
        this.usersReady = true;
      },
      error: () => {
        this.usersLoadError = true;
        this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to load users and permissions.' }));
      }
    });
  }

  private getPermissions(locationID: number) {
    return this.usersService.userManagementListByLocations(new UserPermissionsLocationListRequest({
      IsGroup: false,
      LocationIDs: [locationID]
    }));
  }

  private featuresByUser(result: UserManagementResult, locationID: number): Map<number, FeaturesList> {
    const features = new Map<number, FeaturesList>();
    result.Users.forEach(user => {
      const location = user.Locations.find(item => item.LocationID === locationID);
      if (location) {
        features.set(user.UserID, new FeaturesList(location.Features));
      }
    });
    return features;
  }

  private syncSimplePermissions(features: FeaturesList): void {
    features.SimpleSaveAudit = features.SaveAudit;
    features.SimpleViewAudit = features.ViewAudit || features.SaveAudit;
    features.SimpleCompleteAction = features.CompleteAction;
    features.SimpleViewAction = features.ViewAction || features.CompleteAction;
    features.SimpleAdmin = features.EditUsers || features.EditLocations || features.GroupManagement;
  }

  updatePermission(user: WizardUser, field: keyof FeaturesList, value: boolean): void {
    user.Features[field] = value as never;
    if (field === 'SimpleSaveAudit' && value) {
      user.Features.SimpleViewAudit = true;
    }
    if (field === 'SimpleCompleteAction' && value) {
      user.Features.SimpleViewAction = true;
    }
  }

  saveUsersAndPermissions(): void {
    if (this.saving || !this.createdLocation || this.selectedUsers.length === 0) {
      return;
    }
    const location = new UserLocation(this.createdLocation);
    location.Users = this.selectedUsers.map(user => new BasicUser({ ...user, UserTypeID: 1 }));
    const permissions = this.buildPermissionsPayload();
    this.saving = true;
    this.usersService.saveUserLocationChange(location).pipe(
      switchMap(saved => {
        if (!saved) {
          return of(null);
        }
        // The creator's baseline permissions are granted transactionally during creation and remain unchanged.
        return permissions.Users.length === 0
          ? of(new UserManagementResult({ IsError: false }))
          : this.usersService.saveUserManagementList(permissions);
      }),
      finalize(() => this.saving = false)
    ).subscribe(result => {
      if (!result || result.IsError) {
        this.toast.addToastr(new Toastr({ Type: 3, Message: result?.ErrorMessage || 'Unable to save users and permissions.' }));
        return;
      }
      this.toast.addToastr(new Toastr({ Type: 2, Message: 'Users and permissions saved.' }));
      this.currentStep = 4;
    });
  }

  private buildPermissionsPayload(): UserManagementResult {
    const result = new UserManagementResult();
    result.IsGroup = false;
    result.UseSimplePermissions = true;
    result.UPR = new UserPermissionsRequest({
      IsGroup: false,
      IncludeSublocations: false,
      LocationID: -1,
      FullLocationList: [this.createdLocation!.LocationID]
    });
    result.Users = this.selectedUsers.filter(user => !user.Locked).map(user => {
      const permissionUser = new PermissionsUser(user);
      permissionUser.Locations = [new AuditLocation({
        ...this.createdLocation,
        Features: new FeaturesList(user.Features)
      })];
      return permissionUser;
    });
    return result;
  }

  previousStep(): void {
    if (this.currentStep > 2) {
      this.currentStep--;
    }
  }

  finish(): void {
    this.router.navigateByUrl('/settings');
  }
}
